import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Field, NSelect } from "@/components/common";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { VEHICLE_TYPES, dateInput } from "@/lib/fleet";

const nextYear = () => {
  const d = new Date();
  d.setFullYear(d.getFullYear() + 1);
  return dateInput(d);
};
const EMPTY = { customer_id: "", name: "", plate: "", type: "car", brand: "", imei: "", protocol: "gt06", device_model: "", sim_number: "", supports_cutoff: false, speed_limit: 80, expires_at: nextYear() };

export function VehicleFormDialog({ open, onOpenChange, onSaved }) {
  const { isAdmin, isSeller } = useAuth();
  const [f, setF] = useState(EMPTY);
  const [customers, setCustomers] = useState([]);
  const [protocols, setProtocols] = useState([]);
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    if (!open) return;
    setF({ ...EMPTY, expires_at: nextYear() });
    Promise.all([api.get("/admin/customers"), api.get("/admin/protocols")]).then(([c, p]) => {
      setCustomers(c.data);
      setProtocols(p.data);
      setF((x) => ({ ...x, customer_id: c.data[0]?.id || "" }));
    });
  }, [open]);

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...f, speed_limit: Number(f.speed_limit) };
      if (isSeller) delete payload.expires_at;
      await api.post("/admin/vehicles", payload);
      toast.success("Kendaraan & IMEI berhasil didaftarkan");
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const inp = (k, ph, type = "text") => <input type={type} value={f[k]} onChange={(e) => set(k)(e.target.value)} placeholder={ph} className="gt-input" data-testid={`vehicle-form-${k}`} />;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto border-border bg-popover sm:max-w-xl" data-testid="vehicle-form-dialog">
        <DialogHeader><DialogTitle>Tambah Kendaraan & Daftarkan IMEI</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Customer" className="col-span-2">
            <NSelect value={f.customer_id} onChange={set("customer_id")} testId="vehicle-form-customer">
              {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </NSelect>
          </Field>
          <Field label="Nama kendaraan">{inp("name", "Toyota Avanza")}</Field>
          <Field label="Nomor polisi">{inp("plate", "B 1234 ABC")}</Field>
          <Field label="Jenis">
            <NSelect value={f.type} onChange={set("type")} testId="vehicle-form-type">
              {Object.entries(VEHICLE_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </NSelect>
          </Field>
          <Field label="Merk">{inp("brand", "Toyota")}</Field>
          <Field label="IMEI GPS Tracker" className="col-span-2">{inp("imei", "15 digit IMEI")}</Field>
          <Field label="Protokol">
            <NSelect value={f.protocol} onChange={set("protocol")} testId="vehicle-form-protocol">
              {protocols.map((p) => <option key={p.name} value={p.name}>{p.title}</option>)}
            </NSelect>
          </Field>
          <Field label="Model perangkat">{inp("device_model", "Zacro CY06 / GT06N")}</Field>
          <Field label="Nomor SIM">{inp("sim_number", "0812...")}</Field>
          <Field label="Batas kecepatan (km/j)">{inp("speed_limit", "80", "number")}</Field>
          {isAdmin && <Field label="Masa aktif sampai">{inp("expires_at", "", "date")}</Field>}
          <label className="flex items-center gap-2 self-end pb-2 text-sm">
            <input type="checkbox" checked={f.supports_cutoff} onChange={(e) => set("supports_cutoff")(e.target.checked)} className="accent-yellow-500" data-testid="vehicle-form-cutoff" />
            Relay engine cut-off terpasang
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Batal</Button>
          <Button onClick={save} disabled={busy || !f.name || !f.plate || !f.imei} data-testid="vehicle-form-submit">{busy ? "Menyimpan..." : "Simpan"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
